import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { supabase } from "@/lib/supabaseClient";

interface ArticleRow {
  id: number;
  title: string;
  source: string | null;
  article_date: string | null;
  words_num: number | null;
  data_split: string | null;
  score: number | null;
}

interface ArticlePickerModalProps {
  open: boolean;
  categoryId: number | null;
  categoryName: string;
  split: "train" | "test";
  onClose: () => void;
  onSaved: () => void;
}

const splitLabel: Record<"train" | "test", string> = {
  train: "训练",
  test: "测试",
};

export default function ArticlePickerModal({
  open,
  categoryId,
  categoryName,
  split,
  onClose,
  onSaved,
}: ArticlePickerModalProps) {
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (categoryId === null) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("dataset_articles")
        .select("id, title, source, article_date, words_num, data_split, score")
        .eq("category_id", categoryId)
        .order("id");
      if (queryError) throw queryError;
      const rows = (data ?? []) as ArticleRow[];
      setArticles(rows);
      setSelected(rows.filter((row) => row.data_split === split).map((row) => row.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "文章加载失败");
    } finally {
      setLoading(false);
    }
  }, [categoryId, split]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const originalInSplit = useMemo(
    () => articles.filter((row) => row.data_split === split).map((row) => row.id),
    [articles, split]
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const toAdd = selected.filter((id) => !originalInSplit.includes(id));
      const toRemove = originalInSplit.filter((id) => !selected.includes(id));

      if (toAdd.length > 0) {
        const { error: addError } = await supabase
          .from("dataset_articles")
          .update({ data_split: split })
          .in("id", toAdd);
        if (addError) throw addError;
      }
      if (toRemove.length > 0) {
        const { error: removeError } = await supabase
          .from("dataset_articles")
          .update({ data_split: "none" })
          .in("id", toRemove);
        if (removeError) throw removeError;
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败，请重试");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`选择${splitLabel[split]}数据 · ${categoryName}`}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <span className="mr-auto text-xs text-foreground-500">
            已选 <span className="font-medium text-primary-600">{selected.length}</span> 篇 · 共{" "}
            {articles.length} 篇
          </span>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
          >
            取消
          </button>
          <button
            type="button"
            disabled={saving || loading}
            onClick={handleSave}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "保存中..." : "保存选择"}
          </button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg" />
          正在从数据库加载文章...
        </div>
      ) : error ? (
        <div className="flex items-center justify-between rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
          <span>{error}</span>
          <button
            type="button"
            onClick={load}
            className="cursor-pointer whitespace-nowrap text-primary-600 hover:text-primary-700"
          >
            重试
          </button>
        </div>
      ) : articles.length === 0 ? (
        <p className="py-10 text-center text-sm text-foreground-500">该分类下暂无文章数据</p>
      ) : (
        <div className="divide-y divide-background-100">
          {articles.map((article) => {
            const checked = selected.includes(article.id);
            const inOtherSplit = article.data_split && article.data_split !== "none" && article.data_split !== split;
            return (
              <label
                key={article.id}
                className="flex cursor-pointer items-start gap-3 py-3 hover:bg-background-100/60"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(article.id)}
                  className="mt-0.5 h-4 w-4 cursor-pointer rounded border-background-300 text-primary-500 focus:ring-primary-300"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground-900">{article.title}</span>
                    {inOtherSplit ? (
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800">
                        已被{splitLabel[split === "train" ? "test" : "train"]}选用
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span>{article.source}</span>
                    <span>{article.article_date}</span>
                    <span>{article.words_num} 字符</span>
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
}